import axios from "axios";
import {
  confirmReservation,
  createMailboxReservation,
  getUploadedFileSize,
  uploadFileChunk,
} from "../api";
import {
  CreateMailboxReservationRequest,
  MailboxReservation,
  TransferDraft,
} from "../entities";
import { constants } from "../utils/constants";
import delay from "../utils/delay";

// ===================================================================================

type UploadData = {
  filename: string;
  clientId: string;
  totalBytes: number;
  file: File;
  startByte: number;
  endByte?: number;
};

type UploadProp = {
  reservationUid: string;
  currentChunkSize: number;
  shouldCheckUploadedFileSize: boolean;
  uploadData: UploadData;
  retryCount: number;
  pushProgress: (progress: number, startByte: number) => void;
};

// ===================================================================================

export default async function uploadMailboxTransfer(
  draft: TransferDraft,
  onProgress: (progress: number) => void
): Promise<void> {
  const totalFilesSize = draft.files.reduce(
    (acc, file) => acc + file[0].size,
    0
  );
  onProgress(0);
  var completedUploadSize = 0;
  var currentUploadSize = 0;

  const pushProgress = (uploadedBytes: number, startByte: number) => {
    currentUploadSize = completedUploadSize + startByte + uploadedBytes;
    let uploadProgress: number = Math.floor(
      (currentUploadSize / totalFilesSize) * 100
    );

    // Since, additional bytes of form data is also reported by axios
    // & this additional bytes are reported with different values in different browsers (400 - 500 bytes).
    // There's no reliable way of computing these additional bytes.
    // Therefore for small files (< 50Kb) [uploadProgress] exceeds 100%.
    //
    // Hence, this is done to clip the uploadProgress to not exceed 100%.
    uploadProgress = Math.min(uploadProgress, 100);

    onProgress(uploadProgress);
  };

  // prepare reservation data from transfer draft
  const createRequest: CreateMailboxReservationRequest = {
    subject: draft.subject,
    description: draft.description,
    deliveryNotification: draft.acknowledgement,
    ttl: draft.ttl,
    priority: draft.priority,
    protection: draft.protectionType,
    password: draft.password,
    anonSender: draft.anon.name,
    anonEmail: draft.anon.email,
  };

  // make reservation
  const reservation: MailboxReservation = await createMailboxReservation({
    key: draft.receivers![0].uid!,
    request: createRequest,
  });

  // upload all files in chunks
  for (const [{ name, size, id }, blob] of draft.files) {
    await initiateUpload({
      currentChunkSize: constants.initialChunkSize,
      uploadData: {
        clientId: id,
        file: blob,
        filename: name,
        startByte: 0,
        totalBytes: size,
      },
      reservationUid: reservation.uid,
      shouldCheckUploadedFileSize: false,
      retryCount: 0,
      pushProgress: pushProgress,
    });
    completedUploadSize += size;
  }

  // confirm reservation.
  await confirmReservation(reservation.uid);
}

/**
 *  Initiates the upload process.
 *
 *  If transfer upload is interupted then it will retry uploading transfer
 *  upto {@link MAX_RETRIES} times. If it still fails, to upload, then it will
 *  abort the upload & throw error.
 */
async function initiateUpload({
  reservationUid,
  uploadData,
  shouldCheckUploadedFileSize,
  currentChunkSize,
  retryCount,
  pushProgress,
}: UploadProp): Promise<void> {
  if (shouldCheckUploadedFileSize) {
    // Check how many bytes of file are already uploaded on the server.
    try {
      uploadData.startByte = await getUploadedFileSize({
        reservationUid: reservationUid,
        clientId: uploadData.clientId,
      });
    } catch (error) {
      if (retryCount >= constants.maxUploadRetries) {
        throw error;
      } else {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          // File was never uploaded on the server.
          uploadData.startByte = 0;
        } else {
          return await retryUpload({
            reservationUid,
            uploadData,
            shouldCheckUploadedFileSize,
            currentChunkSize,
            retryCount,
            pushProgress,
          });
        }
      }
    }
  }

  // Compute end byte for uploadData.
  uploadData.endByte = Math.min(
    uploadData.startByte + currentChunkSize,
    uploadData.totalBytes
  );

  // Actual upload of file chunk.
  try {
    const chunkUploadStartTime: number = Date.now();
    await uploadFileChunk({
      clientId: uploadData.clientId,
      startByte: uploadData.startByte,
      endByte: uploadData.endByte,
      totalBytes: uploadData.totalBytes,
      file: uploadData.file,
      filename: uploadData.filename,
      reservationUid: reservationUid,
      onUploadProgress: (progressEvent) => {
        pushProgress(progressEvent.loaded, uploadData.startByte);
      },
    });

    const uploadedBytes = uploadData.endByte;

    // Reset retry counter.
    retryCount = 0;

    // Time (in seconds) took to upload one chunk
    const chunkUploadDuration: number = Math.floor(
      (Date.now() - chunkUploadStartTime) / 1000
    );

    const newChunkSize = calculateAndGetNewChunkSize(
      chunkUploadDuration,
      currentChunkSize
    );

    if (uploadedBytes < uploadData.totalBytes) {
      // upload is not done yet, do another recursion
      uploadData.startByte = uploadedBytes;

      return await initiateUpload({
        currentChunkSize: newChunkSize,
        shouldCheckUploadedFileSize: false,
        reservationUid: reservationUid,
        uploadData: uploadData,
        retryCount: retryCount,
        pushProgress: pushProgress,
      });
    } else {
      // done with this file!
      return;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      // some limitations have most likely been exceeded; no point in retrying the rest
      throw error;
    } else if (retryCount < constants.maxUploadRetries) {
      // do another retry attempt
      return await retryUpload({
        reservationUid,
        uploadData,
        shouldCheckUploadedFileSize,
        currentChunkSize,
        retryCount,
        pushProgress,
      });
    } else {
      // exhausted all retry attempts; giving up
      throw error;
    }
  }
}

async function retryUpload(retryUploadProp: UploadProp): Promise<void> {
  retryUploadProp.retryCount++;

  const waitingTimeMs = Math.min(
    2 ** retryUploadProp.retryCount * 1000,
    constants.maxDelayBetweenRetriesMs
  );

  // Wait for [sleepTimeMs] seconds before retrying chunk upload again.
  await delay(waitingTimeMs);

  retryUploadProp.uploadData.startByte = 0;
  retryUploadProp.shouldCheckUploadedFileSize = true;

  // Retry chunk upload again.
  return await initiateUpload(retryUploadProp);
}

/**
 * Returns new chunk size based on time it took to upload a chunk.
 *
 * @param chunkUploadDuration
 */
const calculateAndGetNewChunkSize = (
  chunkUploadDuration: number, // seconds
  currentChunkSize: number
) => {
  const chunkFactor: number =
    constants.idealChunkUploadDuration / chunkUploadDuration;

  const optimalChunkSize: number = Math.round(currentChunkSize * chunkFactor);

  const clampedChunkSize: number = Math.min(
    optimalChunkSize,
    constants.maxChunkSize
  );

  return clampedChunkSize;
};
